import {
  Description,
  DialogPanel,
  DialogTitle,
  Dialog as HeadlessUiDialog,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import cn from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';

import { FormWithShortcuts } from 'components/FormWithShortcuts';
import { IconAlertTriangle } from 'components/UI/Icons/AlertTriangle';
import { IconLoader } from 'components/UI/Icons/Loader';
import { IconX } from 'components/UI/Icons/X';
import { Button } from 'components/UI/Tailwind/Button';
import {
  atomDialog,
  type ExtractDataType,
  type Dialog as TDialog,
} from 'config/atoms';

type TForm = JSX.IntrinsicElements['form'];

export interface Dialog
  extends
    Pick<
      React.ComponentProps<typeof HeadlessUiDialog>,
      'initialFocus' | 'open'
    >,
    Pick<TForm, 'onSubmit'> {
  actionButton?: React.ReactNode;
  actionButtonProps?: Partial<React.ComponentProps<typeof Button>>;
  /** Passing `backgroundClassName` will override the default background styles! */
  backgroundClassName?: string;
  body?: React.ReactNode;
  bodyClassName?: string;
  buttons?: React.ReactNode;
  cancelButton?: React.ReactNode | (() => React.ReactNode);
  cancelButtonProps?: Partial<React.ComponentProps<typeof Button>>;
  children?: React.ReactNode;
  className?: TForm['className'];
  error?: string;
  /**
   * PostHog form id. Tags the Dialog's <form> with `form-id` for interaction
   * autocapture. Submission is captured explicitly by the consumer via
   * `trackFormSubmit` (wrap the handler passed to `onSubmit`), not here.
   */
  formTrackId?: string;
  heading: string | React.ReactNode;
  iconStart?: React.ReactNode;
  id?: TDialog['id'];
  invertButtons?: boolean;
  isLoading?: boolean | React.ReactNode;
  isSubmitting?: boolean;
  onClose?: VoidFunction;
  /** Passing this prop will override the default max-h and max-w! */
  sizeClassName?: string;
  subtitle?: string | React.ReactNode;
}

export const useDialogData = <T extends Exclude<TDialog['id'], undefined>>(
  dialog: TDialog,
  id: T,
) => {
  const [data, setData] = useState<ExtractDataType<TDialog, T>>();

  useEffect(() => {
    if (dialog.id === id && 'data' in dialog) {
      setData(dialog.data as ExtractDataType<TDialog, T>);
    }
  }, [dialog, id]);

  return data;
};

export const useDialog = ({ id, open }: Pick<Dialog, 'id' | 'open'>) => {
  const setDialog = useSetRecoilState(atomDialog);

  const navigate = useNavigate();
  const search = useSearch({
    strict: false,
  });
  const dialogParam = 'dialog' in search ? search.dialog : undefined;

  const onClose = useCallback(() => {
    setDialog({ id: undefined });
    navigate({
      search: (prev) => ({
        ...prev,
        dialog: undefined,
      }),
    });
  }, [navigate, setDialog]);

  const isOpen = open ?? (id && dialogParam === id);

  return {
    isOpen,
    onClose,
  };
};

export function Dialog({
  actionButton: _actionButton,
  actionButtonProps,
  backgroundClassName,
  body,
  bodyClassName,
  buttons,
  cancelButton: _cancelButton,
  cancelButtonProps = {},
  children,
  error,
  formTrackId,
  heading,
  iconStart,
  id,
  initialFocus: _initialFocus,
  invertButtons,
  isLoading,
  isSubmitting,
  onClose: _onClose,
  onSubmit: _onSubmit,
  open,
  sizeClassName,
  subtitle,
}: Dialog) {
  const { t } = useTranslation();

  const refButton = useRef<HTMLButtonElement>(null);

  const { isOpen, onClose: defaultOnClose } = useDialog({
    id,
    open,
  });

  const onClose = () => {
    if (_onClose) {
      _onClose();
    } else {
      defaultOnClose();
    }
  };

  const onSubmit: Dialog['onSubmit'] = (event) => {
    event.preventDefault();
    _onSubmit?.(event);
  };

  if (!_initialFocus) {
    if (
      !cancelButtonProps.hidden &&
      actionButtonProps?.variant === 'destructive-primary'
    ) {
      cancelButtonProps.ref = refButton;
    } else if (!cancelButtonProps.hidden && actionButtonProps) {
      actionButtonProps.ref = refButton;
    }
  }

  const defaultActionButton = (
    // @ts-ignore
    <Button
      disabled={
        isSubmitting || Boolean(isLoading) || actionButtonProps?.isLoading
      }
      size={40}
      type="submit"
      variant="default-primary"
      name="dialogActionButton"
      {...actionButtonProps}
    >
      {actionButtonProps?.children || t('prompts.close')}
    </Button>
  );

  const defaultCancelButton = (
    // @ts-ignore
    <Button
      disabled={isSubmitting}
      onClick={onClose}
      size={40}
      variant="default-tertiary"
      {...cancelButtonProps}
    >
      {cancelButtonProps.children || t('prompts.cancel')}
    </Button>
  );

  const actionButton = actionButtonProps ? defaultActionButton : _actionButton;

  const cancelButton = actionButton
    ? _cancelButton || defaultCancelButton
    : null;

  const initialFocus = _initialFocus ?? refButton;

  // Sticky header must share the panel's background, otherwise long scrolling
  // content bleeds through the (previously transparent) header.
  const panelBackground = backgroundClassName || 'bg-gray-50';

  return (
    <Transition show={isOpen}>
      <HeadlessUiDialog
        className="relative z-[100]"
        initialFocus={initialFocus}
        onClose={onClose}
      >
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div aria-hidden="true" className="fixed inset-0 bg-gray-900/25" />
        </TransitionChild>
        <div className="fixed inset-0 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                className={cn(
                  'm-2.5 flex w-screen flex-col overflow-y-auto rounded-[12px] border border-gray-300 md:m-6',
                  {
                    'bg-gray-50': !backgroundClassName,
                    'max-h-[664px] max-w-[512px]': !sizeClassName,
                  },
                  backgroundClassName,
                  sizeClassName,
                )}
              >
                {_onSubmit ? (
                  <FormWithShortcuts
                    className={bodyClassName}
                    data-ph-capture-attribute-form-id={formTrackId}
                    onSubmit={onSubmit}
                  >
                    <Header
                      buttons={buttons}
                      className={panelBackground}
                      heading={heading}
                      iconStart={iconStart}
                      isLoading={isLoading}
                      onClose={onClose}
                      subtitle={subtitle}
                    />
                    <Body isLoading={isLoading}>
                      {body ? (
                        <Description className="px-8 text-14/142% font-normal text-gray-700 [&>strong]:font-semibold">
                          {body}
                        </Description>
                      ) : (
                        children
                      )}
                    </Body>
                    {error && (
                      <div className="mx-8 mt-6 bg-red-50 p-2">
                        <span className="flex items-center justify-center gap-2 text-12 font-medium text-red-600 [&>:first-child]:shrink-0 [&>svg]:h-3 [&>svg]:w-3">
                          <IconAlertTriangle />
                          {error}
                        </span>
                      </div>
                    )}
                    {(actionButton || !cancelButtonProps.hidden) && (
                      <div
                        className={cn('flex items-center gap-x-2 px-8 py-6', {
                          'flex-row justify-end': !invertButtons,
                          'flex-row-reverse justify-start': invertButtons,
                        })}
                      >
                        {actionButton ? (
                          <>
                            {cancelButton}
                            {actionButton}
                          </>
                        ) : (
                          defaultActionButton
                        )}
                      </div>
                    )}
                  </FormWithShortcuts>
                ) : (
                  <div className={bodyClassName}>
                    <Header
                      buttons={buttons}
                      className={panelBackground}
                      heading={heading}
                      iconStart={iconStart}
                      isLoading={isLoading}
                      onClose={onClose}
                      subtitle={subtitle}
                    />
                    <Body isLoading={isLoading}>
                      {body ? (
                        <Description className="px-8 text-14/142% font-normal text-gray-700 [&>strong]:font-semibold">
                          {body}
                        </Description>
                      ) : (
                        children
                      )}
                    </Body>
                    {!cancelButtonProps.hidden && (
                      <div
                        className={cn('flex items-center gap-x-2 px-8 py-6', {
                          'flex-row justify-end': !invertButtons,
                          'flex-row-reverse justify-start': invertButtons,
                        })}
                      >
                        <Button
                          disabled={Boolean(isLoading)}
                          onClick={onClose}
                          size={40}
                          variant="default-primary"
                          {...cancelButtonProps}
                        >
                          {t('prompts.close')}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </HeadlessUiDialog>
    </Transition>
  );
}

function Body({
  children,
  isLoading,
}: {
  children?: React.ReactNode;
  isLoading: boolean | React.ReactNode | undefined;
}) {
  if (isLoading) {
    if (typeof isLoading === 'boolean') {
      return (
        <div className="flex flex-1 items-center justify-center p-6 text-gray-600 [&>svg]:h-6 [&>svg]:w-6 [&>svg]:animate-spin">
          <IconLoader />
        </div>
      );
    }

    return isLoading;
  }

  return children;
}

function Header({
  buttons,
  className,
  heading,
  iconStart,
  isLoading,
  onClose,
  subtitle,
}: {
  buttons?: React.ReactNode;
  className?: string;
  heading: string | React.ReactNode;
  iconStart?: React.ReactNode;
  isLoading?: boolean | React.ReactNode;
  onClose: VoidFunction;
  subtitle?: string | React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'sticky top-0 z-10 flex items-center justify-between gap-x-3 px-8',
        className,
        {
          'pt-6': !heading || typeof heading !== 'string',
          'py-[26px]': heading && typeof heading === 'string',
        },
      )}
    >
      <div className="flex flex-col gap-y-1">
        <DialogTitle className="flex flex-nowrap items-center gap-x-3 text-22/127% font-semibold text-gray-800">
          {iconStart && (
            <span className="text-gray-500 [&>svg]:h-6 [&>svg]:w-6">
              {iconStart}
            </span>
          )}
          {isLoading ? null : heading}
        </DialogTitle>
        {subtitle && (
          <span className="text-14/100% font-normal text-gray-600">
            {subtitle}
          </span>
        )}
      </div>

      <div className="flex gap-x-2 self-stretch justify-self-end">
        {buttons}
        <Button
          className="[&>svg]:text-gray-500"
          iconStart={<IconX />}
          onClick={onClose}
          size={26}
          variant="gray-quaternary"
        />
      </div>
    </div>
  );
}
